import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';

// Auth & Theme
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ToastProvider } from './contexts/ToastContext';
import { ConfirmProvider } from './contexts/ConfirmContext';
import ProtectedRoute from './components/ProtectedRoute';
import OnboardingGate from './components/OnboardingGate';
import ErrorBoundary from './components/ErrorBoundary';
import ApiErrorBridge from './components/ApiErrorBridge';

// Components
import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';
import VerifyBanner from './components/VerifyBanner';

// Pages
import Dashboard from './pages/Dashboard';
import Jobs from './pages/Jobs';
import Resumes from './pages/Resumes';
import Documents from './pages/Documents';
import MasterProfile from './pages/MasterProfile';
import Analysis from './pages/Analysis';
import Companies from './pages/Companies';
import Settings from './pages/Settings';
import Tracker from './pages/Tracker';
import Intelligence from './pages/Intelligence';
import LinkedInOptimizer from './pages/LinkedInOptimizer';
import SkillGapAnalysis from './pages/SkillGapAnalysis';
import JobTargeting from './pages/JobTargeting';
import Interview from './pages/Interview';
import Analytics from './pages/Analytics';
import Login from './pages/Login';
import Setup from './pages/Setup';
import Signup from './pages/Signup';
import VerifyEmail from './pages/VerifyEmail';
import AdminUsers from './pages/AdminUsers';
import AdminTelemetry from './pages/AdminTelemetry';
import StartHere from './pages/StartHere';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const pageTransition = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
  transition: { duration: 0.3 },
};

function AppLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-screen overflow-hidden bg-surface-light dark:bg-navy-900">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Navbar />
        <VerifyBanner />
        <main className="flex-1 overflow-y-auto p-6 custom-scrollbar">
          <AnimatePresence mode="wait">
            {children}
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
      <ToastProvider>
      <Router>
        <AuthProvider>
          <ConfirmProvider>
          <ApiErrorBridge />
          <Routes>
            {/* Public routes — no sidebar/navbar */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/setup" element={<Setup />} />

            {/* Protected routes — with sidebar/navbar */}
            <Route path="/start-here" element={
              <ProtectedRoute>
                <AppLayout>
                  <motion.div {...pageTransition}><StartHere /></motion.div>
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/" element={
              <ProtectedRoute>
                <AppLayout>
                  <motion.div {...pageTransition}><Dashboard /></motion.div>
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/jobs" element={
              <ProtectedRoute>
                <AppLayout>
                  <motion.div {...pageTransition}><Jobs /></motion.div>
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/resumes" element={
              <ProtectedRoute>
                <AppLayout>
                  <motion.div {...pageTransition}><Resumes /></motion.div>
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/documents" element={
              <ProtectedRoute>
                <AppLayout>
                  <motion.div {...pageTransition}><Documents /></motion.div>
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/master-profile" element={
              <ProtectedRoute>
                <AppLayout>
                  <motion.div {...pageTransition}><MasterProfile /></motion.div>
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/analysis" element={
              <ProtectedRoute>
                <AppLayout>
                  <motion.div {...pageTransition}><Analysis /></motion.div>
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/analytics" element={
              <ProtectedRoute>
                <AppLayout>
                  <motion.div {...pageTransition}><Analytics /></motion.div>
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/intelligence" element={
              <ProtectedRoute>
                <AppLayout>
                  <motion.div {...pageTransition}><Intelligence /></motion.div>
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/linkedin-optimizer" element={
              <ProtectedRoute>
                <AppLayout>
                  <motion.div {...pageTransition}><LinkedInOptimizer /></motion.div>
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/skill-gap-analysis" element={
              <ProtectedRoute>
                <AppLayout>
                  <motion.div {...pageTransition}><SkillGapAnalysis /></motion.div>
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/job-targeting" element={
              <ProtectedRoute>
                <AppLayout>
                  <OnboardingGate requires={['profile', 'resume']}>
                    <motion.div {...pageTransition}><JobTargeting /></motion.div>
                  </OnboardingGate>
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/interview" element={
              <ProtectedRoute>
                <AppLayout>
                  <motion.div {...pageTransition}><Interview /></motion.div>
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/companies" element={
              <ProtectedRoute>
                <AppLayout>
                  <motion.div {...pageTransition}><Companies /></motion.div>
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/tracker" element={
              <ProtectedRoute>
                <AppLayout>
                  <motion.div {...pageTransition}><Tracker /></motion.div>
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/settings" element={
              <ProtectedRoute>
                <AppLayout>
                  <motion.div {...pageTransition}><Settings /></motion.div>
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/users" element={
              <ProtectedRoute requireAdmin>
                <AppLayout>
                  <motion.div {...pageTransition}><AdminUsers /></motion.div>
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/telemetry" element={
              <ProtectedRoute requireAdmin>
                <AppLayout>
                  <motion.div {...pageTransition}><AdminTelemetry /></motion.div>
                </AppLayout>
              </ProtectedRoute>
            } />
          </Routes>
          </ConfirmProvider>
        </AuthProvider>
      </Router>
      </ToastProvider>
      </ThemeProvider>
    </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
